"use client";

import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Pricing() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: reassuranceRef,
    isVisible: reassuranceVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const starterFeatures = ["Revenue Health Check", "OTA Listing Audit", "Basic Rate Analysis", "Monthly Performance Report", "Email Support", "Standard Tech Guidance"];
  const proFeatures = ["Full Revenue Management", "Dynamic Pricing Strategy", "OTA Channel Optimization", "Direct Booking Campaigns", "Tech Stack Implementation", "Rate Parity Monitoring", "Competitor Benchmarking", "Priority Consultation", "Weekly Strategy Calls"];
  const agencyFeatures = ["Multi-Property Management", "Enterprise Revenue Audits", "Custom Tech Integration", "Dedicated Account Manager", "Advanced Forecasting", "Market Expansion Strategy", "API Data Access", "24/7 Priority Support"];
  const reassurance = ["Free Initial Audit", "No Long-term Lock-in", "Performance-Based Options"];
  return <section data-section-id="124" id="servicetiers" className="relative overflow-hidden bg-background py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden="true">
        <div className="absolute left-1/2 top-0 h-[500px] w-[800px] -translate-x-1/2 rounded-lg bg-[radial-gradient(ellipse_at_center,var(--primary)_0%,transparent_70%)] opacity-20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[600px] rounded-lg bg-[radial-gradient(ellipse_at_center,var(--secondary)_0%,transparent_70%)] opacity-10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground font-default uppercase tracking-wider">
            <Sparkles className="size-3 text-secondary" />
            Consulting Packages
          </div>
          <h2 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Revenue Solutions for Every Hotel
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
            Professional revenue management strategies designed to maximize your property's profitability and market share.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`mx-auto mt-20 grid max-w-md grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3 lg:items-center transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div data-index={0} className="relative flex flex-col rounded-lg border border-border bg-card p-10 shadow-md">
            <div className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              Essential
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-default text-6xl font-bold tracking-tight text-foreground">
                $499
              </span>
              <span className="text-base text-muted-foreground">/mo</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Ideal for independent hotels looking to establish a solid revenue foundation.
            </p>

            <Separator className="my-8 bg-border" />

            <ul className="flex flex-1 flex-col gap-4">
              {starterFeatures.map((feature, i) => <li data-index={i} key={i} className="flex items-start gap-3 text-sm text-foreground">
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary-foreground">
                    <Check className="size-3" strokeWidth={3} />
                  </span>
                  <span className="leading-relaxed">{feature}</span>
                </li>)}
            </ul>

            <Button asChild variant="outline" size="lg" className="mt-10 w-full border-border bg-transparent text-foreground hover:bg-accent">
              <Link to="/ContactPage">Get Started</Link>
            </Button>
          </div>

          {}
          <div data-index={1} className="relative flex flex-col lg:scale-105">
            {}
            <div className="relative rounded-lg p-[1.5px] bg-secondary">
              <div className="relative rounded-lg bg-card p-10">
                {}
                <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-secondary/10 via-transparent to-primary/10" aria-hidden="true" />

                <div className="relative">
                  {}
                  <div className="absolute -top-[68px] left-1/2 -translate-x-1/2">
                    <div className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground shadow-md">
                      <Sparkles className="size-3" />
                      Recommended
                    </div>
                  </div>

                  <div className="font-default text-xs uppercase tracking-widest text-secondary">
                    Growth
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-default text-6xl font-bold tracking-tight text-foreground">
                      $1299
                    </span>
                    <span className="text-base text-muted-foreground">/mo</span>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    Comprehensive management for hotels ready to scale revenue and optimize operations.
                  </p>

                  <Separator className="my-8 bg-border" />

                  <ul className="flex flex-col gap-4">
                    {proFeatures.map((feature, i) => <li data-index={i} key={i} className="flex items-start gap-3 text-sm text-foreground">
                        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary-foreground shadow">
                          <Check className="size-3" strokeWidth={3} />
                        </span>
                        <span className="leading-relaxed">{feature}</span>
                      </li>)}
                  </ul>

                  <Button asChild size="lg" className="mt-10 w-full bg-secondary text-primary-foreground shadow-md hover:opacity-90">
                    <Link to="/ContactPage">Book Consultation</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {}
          <div data-index={2} className="relative flex flex-col overflow-hidden rounded-lg border border-border bg-card p-10 shadow-md">
            {}
            <div className="pointer-events-none absolute inset-0 opacity-[0.15]" aria-hidden="true" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, var(--muted-foreground) 1px, transparent 0)",
            backgroundSize: "24px 24px"
          }} />

            <div className="relative">
              <div className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                Enterprise
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-default text-6xl font-bold tracking-tight text-foreground">
                  Custom
                </span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Tailored solutions for hotel groups and resorts requiring dedicated revenue expertise.
              </p>

              <Separator className="my-8 bg-border" />

              <ul className="flex flex-1 flex-col gap-4">
                {agencyFeatures.map((feature, i) => <li data-index={i} key={i} className="flex items-start gap-3 text-sm text-foreground">
                    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary-foreground">
                      <Check className="size-3" strokeWidth={3} />
                    </span>
                    <span className="leading-relaxed">{feature}</span>
                  </li>)}
              </ul>

              <Button asChild variant="outline" size="lg" className="mt-10 w-full border-border bg-transparent text-foreground hover:bg-accent">
                <Link to="/ContactPage">Contact Sales</Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div ref={reassuranceRef} className={`mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 transition-all duration-700 ease-out ${reassuranceVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {reassurance.map((item, i) => <div data-index={i} key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="flex size-5 items-center justify-center rounded-lg bg-secondary text-primary-foreground">
                <Check className="size-3" strokeWidth={3} />
              </span>
              <span>{item}</span>
            </div>)}
        </div>
      </div>
    </section>;
}